import React, { useState } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../context/ThemeContext';
import { useEncryption } from '../context/EncryptionContext';
import { decryptData, encryptData } from '../utils/encryption';
import { isV2MarkdownExport, isV3MarkdownExport, parseLegacyMarkdown, parseMarkdownV2, parseMarkdownV3 } from '../utils/markdownNotes';
import { sanitizeNoteHtml } from '../utils/sanitizeHtml';
import { importVaultNotes } from '../firebase/vaultImport';

const ImportModal = ({ isOpen, onRequestClose, onImport }) => {
  const { theme, themes } = useTheme();
  const isLightTheme = themes[theme]?.mode === 'light';
  const { isEncryptionEnabled, encryptionKey, encryptionPolicy, isEncryptionLocked, verifyCloudKeyBackup } = useEncryption();
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedNotes, setParsedNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isV3Import, setIsV3Import] = useState(false);
  const [backupPassword, setBackupPassword] = useState('');

  const handleFile = async (file) => {
    if (!file || (!file.name.endsWith('.md') && !file.name.endsWith('.txt'))) {
      setError('Please select a valid Markdown (.md) or Text (.txt) file.');
      return;
    }

    setSelectedFile(file);
    setIsLoading(true);
    setError(null);

    try {
      const content = await file.text();
      const v3 = file.name.endsWith('.md') && isV3MarkdownExport(content);
      const notes = file.name.endsWith('.md') 
        ? (v3 ? parseMarkdownV3(content) : isV2MarkdownExport(content) ? parseMarkdownV2(content, encryptionPolicy) : parseLegacyMarkdown(content))
        : parsePlainTextContent(content);
      setParsedNotes(notes);
      setIsV3Import(v3);
      setBackupPassword('');
    } catch (err) {
      setParsedNotes([]);
      setIsV3Import(false);
      setError(err.message || 'Failed to read file. Please make sure it is a valid text or Markdown file.');
      console.error('Import error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await handleFile(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFile(file);
    }
  };

  const parsePlainTextContent = (content) => {
    // Split by double newline to separate notes
    const sections = content.split(/\n\s*\n/).filter(Boolean);
    
    return sections.map(section => {
      const lines = section.trim().split('\n');
      const title = lines[0].trim();
      const content = lines.slice(1).join('\n').trim();
      
      // Try to detect categories and tags from the content
      let category = '';
      let tags = [];
      
      // Look for category indicators like "Category:", "Type:", "Group:"
      const categoryMatch = content.match(/^(Category|Type|Group):\s*([^\n]+)/i);
      if (categoryMatch) {
        category = categoryMatch[2].trim();
      }
      
      // Look for tag indicators like "Tags:", "Labels:", "#tag"
      const tagsMatch = content.match(/^Tags:\s*([^\n]+)/i);
      if (tagsMatch) {
        tags = tagsMatch[1].split(',').map(tag => tag.trim());
      } else {
        // Look for hashtags in the content
        const hashTags = content.match(/#[\w-]+/g);
        if (hashTags) {
          tags = hashTags.map(tag => tag.substring(1));
        }
      }
      
      return {
        title: title || 'Untitled Note',
        content: content || '',
        category,
        tags,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    });
  };

  const handleImport = async () => {
    if (isEncryptionLocked) {
      setError('Import is temporarily unavailable while encryption is being verified or changed.');
      return;
    }
    if (parsedNotes.length > 0) {
      setIsLoading(true);
      setError(null);
      try {
        if (isV3Import) {
          const eligibleProfile = encryptionPolicy?.kind === 'legacy' || (encryptionPolicy?.kind === 'managed' && encryptionPolicy.mode === 'disabled' && Number.isInteger(encryptionPolicy.generation) && encryptionPolicy.generation >= 0 && !encryptionPolicy.transition);
          if (!eligibleProfile || isEncryptionEnabled || !encryptionKey || !backupPassword || typeof verifyCloudKeyBackup !== 'function' || !await verifyCloudKeyBackup(backupPassword)) {
            throw new Error('Vault import requires an eligible unlocked key and a verified cloud-key backup.');
          }
          for (const note of parsedNotes.filter((item) => item.protection === 'vault')) {
            try {
              const plaintext = await decryptData(note.content.slice('encrypted:'.length), encryptionKey);
              if (typeof plaintext !== 'string') throw new Error('Invalid Vault ciphertext.');
            } catch (_) { throw new Error('Unable to verify Vault ciphertext with the active key.'); }
          }
          await importVaultNotes(parsedNotes.map(({ isEncryptedContent, isLegacyEncryptedContent, createdAt, updatedAt, ...note }) => note));
          setBackupPassword('');
          setSelectedFile(null); setParsedNotes([]); setIsV3Import(false);
          onRequestClose();
          return;
        }
        if (isEncryptionEnabled && !encryptionKey) {
          throw new Error('Encryption is enabled, but no encryption key is available.');
        }
        const notesForImport = await Promise.all(parsedNotes.map(async note => {
          const { isEncryptedContent, isLegacyEncryptedContent, ...noteForImport } = note;
          if (isEncryptedContent) {
            if (!encryptionKey) {
              throw new Error('Encrypted import content requires an available encryption key for verification.');
            }
            let decryptedContent;
            try {
              decryptedContent = await decryptData(note.content.slice('encrypted:'.length), encryptionKey);
              if (typeof decryptedContent !== 'string') throw new Error('No decrypted content returned.');
            } catch (error) {
              throw new Error('Unable to verify encrypted import content. Check that the encryption key matches and the export is not corrupted.');
            }
            if (encryptionPolicy?.kind === 'managed' && encryptionPolicy.mode === 'disabled' && !isLegacyEncryptedContent) {
              return { ...noteForImport, content: sanitizeNoteHtml(decryptedContent) };
            }
            return noteForImport;
          }
          if (!isEncryptionEnabled && note.content?.startsWith('encrypted:') && !(encryptionPolicy?.kind === 'managed' && encryptionPolicy.mode === 'disabled')) {
            throw new Error('Plaintext import content cannot start with encrypted: while encryption is disabled.');
          }
          if (isEncryptionEnabled) {
            const encryptedContent = await encryptData(note.content || '', encryptionKey);
            return { ...noteForImport, content: `encrypted:${encryptedContent}` };
          }
          return noteForImport;
        }));
        await onImport(notesForImport);
        setSelectedFile(null);
        setParsedNotes([]);
        const fileInput = document.getElementById('file-input');
        if (fileInput) fileInput.value = '';
        onRequestClose();
      } catch (err) {
        setError(err.message || 'Failed to import notes. Please try again.');
      } finally {
        setBackupPassword('');
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Import Notes"
      className={`${isLightTheme ? 'bg-white' : 'bg-gray-800'} p-6 rounded-lg shadow-lg w-[800px] max-w-full mx-auto`}
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
    >
      <div>
        <h2 className={`text-2xl font-semibold mb-6 ${isLightTheme ? 'text-gray-800' : 'text-white'}`}>
          Import Notes
        </h2>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
            isDragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
              : isLightTheme
                ? 'border-gray-300'
                : 'border-gray-600'
          }`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept=".md,.txt"
            onChange={handleFileSelect}
            className="hidden"
            id="file-input"
          />
          <label
            htmlFor="file-input"
            className={`flex flex-col items-center cursor-pointer ${
              isLightTheme ? 'text-gray-600' : 'text-gray-300'
            }`}
          >
            <FontAwesomeIcon 
              icon={isLoading ? faSpinner : faFileImport} 
              className={`text-4xl mb-2 ${isLoading ? 'animate-spin' : ''} ${
                isDragging ? 'text-blue-500' : ''
              }`}
            />
            <span className={`text-lg font-medium ${isDragging ? 'text-blue-500' : ''}`}>
              {selectedFile ? selectedFile.name : isDragging 
                ? 'Drop your file here'
                : 'Choose a Markdown or Text file'
              }
            </span>
            <span className={`text-sm mt-1 ${isDragging ? 'text-blue-500' : ''}`}>
              or drag and drop it here
            </span>
            <span className={`text-xs mt-2 ${
              isDragging 
                ? 'text-blue-400'
                : isLightTheme ? 'text-gray-500' : 'text-gray-400'
            }`}>
              Supported formats: .md, .txt
            </span>
          </label>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}
        {isEncryptionLocked && (
          <div role="status" className="mt-4 p-3 bg-yellow-100 text-yellow-800 rounded">
            Import is temporarily unavailable while encryption is being verified or changed.
          </div>
        )}

        {parsedNotes.length > 0 && (
          <div className="mt-6">
            <h3 className={`text-lg font-semibold mb-3 ${isLightTheme ? 'text-gray-800' : 'text-white'}`}>
              Preview ({parsedNotes.length} notes found)
            </h3>
            <div className={`border rounded-lg p-4 max-h-64 overflow-y-auto ${
              isLightTheme ? 'bg-gray-50' : 'bg-gray-900'
            }`}>
              {parsedNotes.map((note, index) => (
                <div
                  key={index}
                  className={`p-3 ${index > 0 ? 'border-t' : ''} ${
                    isLightTheme ? 'border-gray-200' : 'border-gray-700'
                  }`}
                >
                  <h4 className={`font-medium ${isLightTheme ? 'text-gray-800' : 'text-gray-200'}`}>
                    {note.title}
                  </h4>
                  {note.category && (
                    <p className={`text-sm ${isLightTheme ? 'text-gray-600' : 'text-gray-400'}`}>
                      Category: {note.category}
                    </p>
                  )}
                  {note.tags.length > 0 && (
                    <p className={`text-sm ${isLightTheme ? 'text-gray-600' : 'text-gray-400'}`}>
                      Tags: {note.tags.join(', ')}
                    </p>
                  )}
                  <p className={`text-sm mt-1 ${isLightTheme ? 'text-gray-700' : 'text-gray-300'}`}>
                    {note.content.substring(0, 100)}...
                  </p>
                </div>
              ))}
            </div>
            {isV3Import && (
              <div className="mt-4">
                <label htmlFor="vault-import-password" className="block text-sm font-medium">Cloud-key password</label>
                <input id="vault-import-password" type="password" autoComplete="current-password" value={backupPassword} onChange={(event) => setBackupPassword(event.target.value)} className="mt-1 w-full rounded border p-2" />
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onRequestClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
          >
            Cancel
          </button>
          {parsedNotes.length > 0 && (
            <button
              onClick={handleImport}
              disabled={isLoading || isEncryptionLocked || (isV3Import && !backupPassword)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Importing…' : `Import ${parsedNotes.length} notes`}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ImportModal;
